import { Injectable } from '@angular/core';
import { Subject, Observable } from "rxjs";
import { NOTIFICATION_TIMEOUT }
    from '@app/secure/constant/notification.constant';
import { Notification } from "@app/secure/model/Notification";
import { NotificationType } from "@app/secure/model/NotificationType";

@Injectable
({
    providedIn: 'root'
})
class NotificationService 
{
    private subject = new Subject<Notification>();
    private idx = 0;

    constructor() { }

    getObservable(): Observable<Notification> 
    {
        return this.subject.asObservable();
    }

    /**
     * @desc   - Keep a toast up for at least its type minimum; toasts
     *           have no close button, so 0 or short values are raised
     * @access - private
     * @param  - timeout : requested hide delay in ms
     * @param  - minimum : shortest allowed hide delay in ms
     * @return - number hide delay in ms
     */
    private displayTime
    (
        timeout: number,
        minimum: number
    ): number
    {
        let result: number;

        result = Math.max(timeout || 0, minimum);

        return result;
    }

    /**
     * @desc   - Show an info toast; auto-hides after timeout
     * @access - public
     * @param  - title : notification title
     * @param  - message : notification body
     * @param  - timeout : hide delay in ms
     * @return -
     */
    info
    (
        title: string, 
        message: string, 
        timeout: number = NOTIFICATION_TIMEOUT.INFO
    ) 
    {
        this.subject.next(new Notification(this.idx++, 
                                           NotificationType.INFO, title, 
                                           message, 
                                           this.displayTime(
                                               timeout,
                                               NOTIFICATION_TIMEOUT.INFO)));
    }

    /**
     * @desc   - Show a success toast; auto-hides after timeout
     * @access - public
     * @param  - title : notification title
     * @param  - message : notification body
     * @param  - timeout : hide delay in ms (raised to the success minimum)
     * @return -
     */
    success
    (
        title: string, 
        message: string, 
        timeout: number = NOTIFICATION_TIMEOUT.SUCCESS
    ) 
    {
        this.subject.next(new Notification(this.idx++, 
                                            NotificationType.SUCCESS, 
                                            title, message, 
                                            this.displayTime(
                                                timeout,
                                                NOTIFICATION_TIMEOUT
                                                    .SUCCESS)));
    }

    /**
     * @desc   - Show a warning toast; auto-hides after timeout
     * @access - public
     * @param  - title : notification title
     * @param  - message : notification body
     * @param  - timeout : hide delay in ms (raised to the success minimum)
     * @return -
     */
    warning
    (
        title: string, 
        message: string, 
        timeout: number = NOTIFICATION_TIMEOUT.SUCCESS
    ) 
    {
        this.subject.next(new Notification(this.idx++, 
                                           NotificationType.WARNING, 
                                           title, message, 
                                           this.displayTime(
                                               timeout,
                                               NOTIFICATION_TIMEOUT
                                                   .SUCCESS)));
    }

    /**
     * @desc   - Show an error toast; auto-hides after timeout
     * @access - public
     * @param  - title : notification title
     * @param  - message : notification body
     * @param  - timeout : hide delay in ms (raised to the error minimum)
     * @return -
     */
    error
    (
        title: string,
        message: string,
        timeout: number = NOTIFICATION_TIMEOUT.ERROR
    )
    {
        this.subject.next(new Notification(this.idx++,
                                           NotificationType.ERROR,
                                           title, message,
                                           this.displayTime(
                                               timeout,
                                               NOTIFICATION_TIMEOUT.ERROR)));
    }

    /**
     * @desc   - Clear all visible notifications
     * @access - public
     * @param  -
     * @return -
     */
    clear()
    {
        this.subject.next(new Notification(
            this.idx++,
            NotificationType.CLEAR,
            '',
            '',
            0
        ));
    }

    /**
     * @desc   - Close a specific notification by id across all instances
     * @access - public
     * @param  - id : notification id to close
     * @return -
     */
    closeNotification
    (
        id: number
    )
    {
        this.subject.next(new Notification(
            id,
            NotificationType.DISMISS_ONE,
            '',
            '',
            0
        ));
    }
}

export { NotificationService };
